const EMPTY_SNAPSHOT: readonly string[] = [];

let openSet = new Set<string>();
let snapshot: readonly string[] = EMPTY_SNAPSHOT;
const listeners = new Set<() => void>();

let scrollPosition = 0;
let scrollLockEnabled = false;

const updateSnapshot = (): void => {
  snapshot = openSet.size === 0 ? EMPTY_SNAPSHOT : Array.from(openSet);
};

const notify = (): void => {
  updateSnapshot();
  listeners.forEach((l) => {
    l();
  });
};

const enableScrollLock = (): void => {
  if (typeof window === 'undefined') return;
  const htmlElement = document.querySelector('html');
  if (!htmlElement || scrollLockEnabled) return;
  scrollLockEnabled = true;
  scrollPosition = window.pageYOffset;
  // Measured before overflow is hidden below: once the scrollbar is removed, clientWidth grows
  // to fill the space it occupied, which is exactly the shift we're compensating for here.
  const scrollbarWidth = window.innerWidth - htmlElement.clientWidth;
  htmlElement.style.overflow = 'hidden';
  htmlElement.style.width = '100%';
  if (scrollbarWidth > 0) {
    htmlElement.style.paddingRight = `${scrollbarWidth}px`;
  }
};

const disableScrollLock = (): void => {
  if (typeof window === 'undefined') return;
  const htmlElement = document.querySelector('html');
  if (!htmlElement || !scrollLockEnabled) return;
  scrollLockEnabled = false;
  htmlElement.style.removeProperty('overflow');
  htmlElement.style.removeProperty('width');
  htmlElement.style.removeProperty('padding-right');
  window.scrollTo(0, scrollPosition);
};

const syncScrollLock = (): void => {
  if (openSet.size > 0) {
    enableScrollLock();
  } else {
    disableScrollLock();
  }
};

export const modalStore = {
  subscribe: (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },

  getSnapshot: (): readonly string[] => snapshot,

  getServerSnapshot: (): readonly string[] => EMPTY_SNAPSHOT,

  isOpen: (name: string): boolean => openSet.has(name),

  getOpenModals: (): readonly string[] => snapshot,

  openModal: (name: string): void => {
    if (typeof window === 'undefined') return;
    if (openSet.has(name)) return;
    openSet = new Set(openSet).add(name);
    syncScrollLock();
    notify();
  },

  closeModal: (name: string, callback?: () => void): void => {
    if (typeof window === 'undefined') return;

    document.querySelector('#root')?.setAttribute('aria-hidden', 'false');

    if (!openSet.has(name)) {
      if (callback) {
        setTimeout(() => {
          callback();
        }, 100);
      }
      return;
    }

    const next = new Set(openSet);
    next.delete(name);
    openSet = next;
    syncScrollLock();
    notify();

    if (callback) {
      setTimeout(() => {
        callback();
      }, 100);
    }
  },

  /** Test / reset: clears all open modals and scroll lock */
  __resetForTests: (): void => {
    openSet = new Set();
    disableScrollLock();
    notify();
  },
};

updateSnapshot();
